import { Box } from '@mui/material';

export type LogoBackdrop = 'none' | 'square' | 'circle';

interface TenantLogoBadgeProps {
  src: string;
  backdrop?: LogoBackdrop | null;
  /** Displayed size as a percent of the default (50–150). */
  scale?: number | null;
  alt?: string;
}

const BASE_HEIGHT = 30; // default logo height in the top bar (px), used when there is no shape
const SHAPE_SIZE = 40; // fixed square side / circle diameter — fits within the 50px top bar

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

/**
 * Renders a tenant's company logo for the (dark) top bar.
 *
 * - backdrop "none": the logo is scaled directly (no shape, no cropping).
 * - backdrop "square"/"circle": the shape is a FIXED-size frame; the scale zooms
 *   the logo *inside* the shape (contained at 100%, zoomed & cropped above it),
 *   so the badge never changes size — only what's visible within the shape does.
 *
 * Used by both the real Topbar and the admin Branding preview so they stay identical.
 */
export default function TenantLogoBadge({ src, backdrop, scale, alt = 'Company logo' }: TenantLogoBadgeProps) {
  const factor = clamp((scale ?? 100) / 100, 0.5, 1.5);

  if (backdrop === 'square' || backdrop === 'circle') {
    return (
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: backdrop === 'circle' ? '50%' : 1,
          width: SHAPE_SIZE,
          height: SHAPE_SIZE,
          overflow: 'hidden', // crop the logo to the shape
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            maxWidth: SHAPE_SIZE,
            maxHeight: SHAPE_SIZE,
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            transform: `scale(${factor})`,
            transformOrigin: 'center',
          }}
        />
      </Box>
    );
  }

  const h = BASE_HEIGHT * factor;
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{ height: h, width: 'auto', maxWidth: 150 * factor, objectFit: 'contain' }}
    />
  );
}
